"use client";

import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PrimaryActionButton } from "@/components/ui/PrimaryActionButton";
import { YouTubeAccountCard } from "./YouTubeAccountCard";
import { FaYoutube } from "react-icons/fa";
import { GlassPanel } from "@/components/ui/GlassPanel";
import {
    useYouTubeConnections,
    UseYouTubeConnectionsReturn,
} from "../hooks/useYouTubeConnections";

type YouTubeSectionVariant = "page" | "popup";

type YouTubeSectionProps = {
    hook?: UseYouTubeConnectionsReturn;
    variant?: YouTubeSectionVariant;
    onBack?: () => void;
    onConnectMore?: () => void;
    redirectTo?: string;
};

export function YouTubeSection({
    hook: providedHook,
    variant = "page",
    onBack,
    onConnectMore,
    redirectTo,
}: YouTubeSectionProps = {}) {
    const { t } = useTranslation();
    const hook = providedHook ?? useYouTubeConnections();

    const {
        isLoading,
        isConnecting,
        accounts,
        connectYouTube,
        disconnectYouTube,
        isIndexing,
        currentMessageIndex,
        indexingMessages,
        triggerIndex,
    } = hook;

    const sectionClassName = variant === "popup" ? "text-white space-y-6 w-full" : "rounded-lg shadow-sm text-white";
    const hasAccounts = accounts.length > 0;
    const connectMoreTranslation = t("account.connect_more_accounts");
    const connectMoreText =
        connectMoreTranslation && connectMoreTranslation !== "account.connect_more_accounts"
            ? connectMoreTranslation
            : "Connect more accounts";

    const handleConnect = useCallback(() => {
        const fallbackRedirect = typeof window !== "undefined" ? window.location.pathname : "/account";
        return connectYouTube(redirectTo ?? fallbackRedirect);
    }, [connectYouTube, redirectTo]);

    if (isLoading) {
        return (
            <div className={sectionClassName}>
                <div className="flex items-center justify-between mb-4">
                    {variant === "popup" ? (
                        <>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-white"
                                onClick={onBack}
                                disabled={!onBack}
                            >
                                <ArrowLeft className="h-4 w-4" />
                                <span className="sr-only">Back</span>
                            </Button>
                            <h3 className="text-2xl font-normal">YouTube</h3>
                            <div className="h-8 w-8" />
                        </>
                    ) : (
                        <h3 className="text-2xl font-normal mb-4">YouTube</h3>
                    )}
                </div>
                <div className="animate-pulse">
                    <div className="h-4 bg-neutral-800 rounded w-3/4 mb-4"></div>
                    <div className="h-8 bg-neutral-800 rounded w-1/2"></div>
                </div>
            </div>
        );
    }

    const header = (
        <div className="flex items-center justify-between mb-4">
            {variant === "popup" ? (
                <>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-white"
                        onClick={onBack}
                        disabled={!onBack}
                    >
                        <ArrowLeft className="h-4 w-4" />
                        <span className="sr-only">Back</span>
                    </Button>
                    <h3 className="text-2xl font-normal">YouTube</h3>
                    <div className="h-8 w-8" />
                </>
            ) : (
                <h3 className="text-2xl font-normal mb-4">YouTube</h3>
            )}
        </div>
    );

    const content = hasAccounts ? (
        <div className="space-y-4">
            {accounts.map((account) => (
                <YouTubeAccountCard
                    key={account.id}
                    account={account}
                    onDisconnect={disconnectYouTube}
                    isLoading={isLoading}
                    onIndex={triggerIndex}
                    isIndexing={isIndexing}
                    currentMessageIndex={currentMessageIndex}
                    indexingMessages={indexingMessages}
                />
            ))}
        </div>
    ) : (
        <GlassPanel className="text-center p-4 md:p-6 space-y-6 ">
            <FaYoutube className="mx-auto h-12 w-12" />
            <h3 className="text-lg font-medium">
                {t("account.no_youtube_accounts_found") || "No YouTube Accounts Found"}
            </h3>

            <div className="space-y-3 max-w-sm mx-auto mt-6">
                <PrimaryActionButton
                    size="sm"
                    className="w-full"
                    onClick={handleConnect}
                    disabled={isConnecting}
                >
                    <span className="inline-flex items-center justify-center h-5 w-5 rounded-md">
                        <FaYoutube className="h-3.5 w-3.5" />
                    </span>
                    {isConnecting
                        ? t("account.connecting") || "Connecting..."
                        : t("account.connect_with_youtube") || "Connect with YouTube"}
                </PrimaryActionButton>
            </div>
        </GlassPanel>
    );

    const connectMoreButton =
        variant === "popup" && hasAccounts && onConnectMore ? (
            <Button
                type="button"
                variant="ghost"
                className="w-full border border-white/10 bg-white/5 text-white hover:bg-white/10"
                onClick={onConnectMore}
            >
                {connectMoreText}
            </Button>
        ) : null;

    return (
        <div className={sectionClassName}>
            {header}
            {content}
            {connectMoreButton}
        </div>
    );
}
